import { useNavigate, useLocation } from 'react-router-dom'
import AppFooter from './AppFooter'
import { Calendar, ArrowLeft } from 'lucide-react'
import { Button } from './ui/button'
import LanguageToggle from './LanguageToggle'
import ThemeMenu from "./ThemeMenu"
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { formatDate } from '../lib/translations'
import { TERMS_EMAIL, TERMS_WEBSITE } from '../content/termsContent'

const EmailLink = () => (
  <a href={`mailto:${TERMS_EMAIL}`} className="text-[#ff4d8d] hover:underline">
    {TERMS_EMAIL}
  </a>
)

const WebsiteLink = () => (
  <a
    href={TERMS_WEBSITE}
    className="text-[#ff4d8d] hover:underline"
    target="_blank"
    rel="noreferrer"
  >
    Storyfy.ge
  </a>
)

const emphasizeText = (text, phrases = []) => {
  if (typeof text !== 'string') return text ?? null
  if (!phrases?.length) return text

  let result = text
  phrases.forEach((phrase) => {
    result = result.split(phrase).join(`__BOLD__${phrase}__/BOLD__`)
  })

  return result.split(/(__BOLD__.*?__\/BOLD__)/g).map((part, index) => {
    if (part.startsWith('__BOLD__')) {
      return (
        <strong key={index}>
          {part.replace('__BOLD__', '').replace('__/BOLD__', '')}
        </strong>
      )
    }
    return part
  })
}

const ListItem = ({ item }) => (
  <li>
    {item.label ? (
      <>
        <strong>{item.label}</strong>{' '}
        {emphasizeText(item.text, item.emphasize)}
      </>
    ) : (
      emphasizeText(typeof item === 'string' ? item : item.text, item.emphasize)
    )}
  </li>
)

const LegalDocumentPage = ({ contentByLanguage, lastUpdated }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { language } = useLanguage()
  const content = contentByLanguage[language] || contentByLanguage.en

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }
    navigate(user ? '/dashboard' : '/login', { replace: true })
  }

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      <header className="bg-white/70 dark:bg-white/[0.04] backdrop-blur-xl border-b border-gray-200/70 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={handleBack}
                aria-label={language === 'ka' ? 'უკან' : 'Go back'}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 btn-gradient glow-brand rounded-xl flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-gray-900 dark:text-[#f4f0ff] font-display">
                  Storyfy.ge
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageToggle />
              <ThemeMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100dvh-4rem)] max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <article className="bg-white dark:bg-[#1e1438] rounded-bubble border border-gray-100 dark:border-white/10 shadow-celebrate-sm p-6 md:p-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gradient-brand font-display mb-2 pb-1">
            {content.title}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            <strong>{content.lastUpdatedLabel}</strong>{' '}
            {formatDate(lastUpdated, language)}
            {language === 'ka' ? ' წელი' : ''}
          </p>

          {content.intro?.map((paragraph) => (
            <p
              key={paragraph.slice(0, 40)}
              className="text-gray-700 dark:text-gray-400 leading-relaxed mb-6 last:mb-10"
            >
              {paragraph}
            </p>
          ))}

          {content.sections?.map((section) => (
            <section key={section.title} className="mb-10">
              <h2 className="flex items-center gap-2.5 text-lg font-bold text-gray-900 dark:text-[#f4f0ff] font-display mb-4">
                <span
                  aria-hidden="true"
                  className="h-5 w-1 rounded-full bg-gradient-to-b from-[#ff4d8d] to-violet-500"
                />
                {section.title}
              </h2>
              <div className="text-gray-700 dark:text-gray-400 leading-relaxed space-y-4">
                {section.intro && <p>{section.intro}</p>}
                {section.subsections?.map((subsection) => (
                  <div key={subsection.title}>
                    <h3 className="font-semibold text-gray-900 dark:text-[#f4f0ff] mb-2">
                      {subsection.title}
                    </h3>
                    <ul className="list-disc pl-5 space-y-2">
                      {subsection.list?.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                {section.paragraph && <p>{section.paragraph}</p>}
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                ))}
                {section.list && (
                  <ul className="list-disc pl-5 space-y-3">
                    {section.list.map((item) => (
                      <ListItem
                        key={typeof item === 'string' ? item : item.label || item.text}
                        item={item}
                      />
                    ))}
                  </ul>
                )}
                {section.outro && <p>{section.outro}</p>}
                {section.contact && (
                  <div>
                    {section.contact.title && (
                      <p className="font-semibold text-gray-900 dark:text-[#f4f0ff] mb-2">
                        {section.contact.title}
                      </p>
                    )}
                    <ul className="list-none pl-0 space-y-1">
                      <li>
                        {section.contact.emailLabel}{' '}
                        <EmailLink />
                      </li>
                      <li>
                        {section.contact.websiteLabel}{' '}
                        <WebsiteLink />
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </section>
          ))}

          {content.closing && (
            <p className="text-gray-700 dark:text-gray-400 leading-relaxed mt-6 pt-6 border-t border-gray-100 dark:border-white/10">
              {content.closing}
            </p>
          )}
        </article>
      </main>

      <AppFooter maxWidth="max-w-3xl" />
    </div>
  )
}

export default LegalDocumentPage
